import React from 'react';

export type IconProps = {
  size?: number;
  stroke?: number;
  fill?: string;
  style?: React.CSSProperties;
  className?: string;
};

type BaseIconProps = IconProps & {
  d?: string;
  children?: React.ReactNode;
};

const Icon = ({
  d,
  size = 20,
  stroke = 1.6,
  fill = 'none',
  style,
  className,
  children,
}: BaseIconProps) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill={fill}
    stroke="currentColor"
    strokeWidth={stroke}
    strokeLinecap="round"
    strokeLinejoin="round"
    style={style}
    className={className}
    aria-hidden="true"
  >
    {d ? <path d={d} /> : children}
  </svg>
);

export const I = {
  search: (p: IconProps = {}) => (
    <Icon {...p}>
      <circle cx="11" cy="11" r="6.5" />
      <path d="m20 20-3.5-3.5" />
    </Icon>
  ),
  close: (p: IconProps = {}) => <Icon {...p} d="M6 6l12 12M18 6 6 18" />,
  plus: (p: IconProps = {}) => <Icon {...p} d="M12 5v14M5 12h14" />,
  minus: (p: IconProps = {}) => <Icon {...p} d="M5 12h14" />,
  check: (p: IconProps = {}) => <Icon {...p} d="m5 12.5 4.5 4.5L19 7" />,
  copy: (p: IconProps = {}) => (
    <Icon {...p}>
      <rect x="8" y="8" width="12" height="12" rx="2.5" />
      <path d="M16 8V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h2" />
    </Icon>
  ),
  sparkles: (p: IconProps = {}) => (
    <Icon {...p}>
      <path d="M12 4v4M12 16v4M4 12h4M16 12h4" />
      <path d="m6.5 6.5 1.4 1.4M16.1 16.1l1.4 1.4M6.5 17.5l1.4-1.4M16.1 7.9l1.4-1.4" />
    </Icon>
  ),
  star: (p: IconProps = {}) => (
    <Icon
      {...p}
      d="M12 3.5 14.5 9l6 .6-4.5 4 1.3 5.9L12 16.6 6.7 19.5 8 13.6 3.5 9.6l6-.6L12 3.5Z"
    />
  ),
  starFill: (p: IconProps = {}) => (
    <Icon
      {...p}
      fill="currentColor"
      stroke={0}
      d="M12 3.5 14.5 9l6 .6-4.5 4 1.3 5.9L12 16.6 6.7 19.5 8 13.6 3.5 9.6l6-.6L12 3.5Z"
    />
  ),
  folder: (p: IconProps = {}) => (
    <Icon
      {...p}
      d="M3.5 7a2 2 0 0 1 2-2h3.2a2 2 0 0 1 1.4.6l1.4 1.4h6.5a2 2 0 0 1 2 2v8.5a2 2 0 0 1-2 2H5.5a2 2 0 0 1-2-2V7Z"
    />
  ),
  share: (p: IconProps = {}) => (
    <Icon {...p}>
      <path d="M12 3v13" />
      <path d="m7 8 5-5 5 5" />
      <path d="M5 14v5a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-5" />
    </Icon>
  ),
  settings: (p: IconProps = {}) => (
    <Icon {...p}>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 14.3a1.7 1.7 0 0 0 .4 1.9l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.9-.4 1.7 1.7 0 0 0-1 1.5V20a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.9.4l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .4-1.9 1.7 1.7 0 0 0-1.5-1H4a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1 1.7 1.7 0 0 0-.4-1.9l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.9.4H10a1.7 1.7 0 0 0 1-1.5V4a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.9-.4l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.4 1.9V10a1.7 1.7 0 0 0 1.5 1H20a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1Z" />
    </Icon>
  ),
  user: (p: IconProps = {}) => (
    <Icon {...p}>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 20a8 8 0 0 1 16 0" />
    </Icon>
  ),
  lock: (p: IconProps = {}) => (
    <Icon {...p}>
      <rect x="4.5" y="10.5" width="15" height="10" rx="2.5" />
      <path d="M8 10.5V7a4 4 0 1 1 8 0v3.5" />
    </Icon>
  ),
  mail: (p: IconProps = {}) => (
    <Icon {...p}>
      <rect x="3.5" y="5" width="17" height="14" rx="2.5" />
      <path d="m4 7 8 6 8-6" />
    </Icon>
  ),
  eye: (p: IconProps = {}) => (
    <Icon {...p}>
      <path d="M2.5 12s3.5-7 9.5-7 9.5 7 9.5 7-3.5 7-9.5 7-9.5-7-9.5-7Z" />
      <circle cx="12" cy="12" r="3" />
    </Icon>
  ),
  eyeOff: (p: IconProps = {}) => (
    <Icon {...p}>
      <path d="M4 4l16 16" />
      <path d="M9.6 5.4A10 10 0 0 1 12 5c6 0 9.5 7 9.5 7a14 14 0 0 1-3 3.5" />
      <path d="M6.7 6.7C4 8.6 2.5 12 2.5 12s3.5 7 9.5 7c1.5 0 2.9-.4 4.2-1" />
      <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" />
    </Icon>
  ),
  home: (p: IconProps = {}) => (
    <Icon {...p} d="M4 11.5 12 4l8 7.5V20a1 1 0 0 1-1 1h-4v-6h-6v6H5a1 1 0 0 1-1-1v-8.5Z" />
  ),
  list: (p: IconProps = {}) => (
    <Icon {...p}>
      <path d="M9 6h12M9 12h12M9 18h12" />
      <circle cx="4.5" cy="6" r="1" fill="currentColor" stroke="none" />
      <circle cx="4.5" cy="12" r="1" fill="currentColor" stroke="none" />
      <circle cx="4.5" cy="18" r="1" fill="currentColor" stroke="none" />
    </Icon>
  ),
  grid: (p: IconProps = {}) => (
    <Icon {...p}>
      <rect x="4" y="4" width="7" height="7" rx="1.5" />
      <rect x="13" y="4" width="7" height="7" rx="1.5" />
      <rect x="4" y="13" width="7" height="7" rx="1.5" />
      <rect x="13" y="13" width="7" height="7" rx="1.5" />
    </Icon>
  ),
  chevR: (p: IconProps = {}) => <Icon {...p} d="m9 5 7 7-7 7" />,
  chevL: (p: IconProps = {}) => <Icon {...p} d="m15 5-7 7 7 7" />,
  chevDown: (p: IconProps = {}) => <Icon {...p} d="m5 9 7 7 7-7" />,
  chevUp: (p: IconProps = {}) => <Icon {...p} d="m5 15 7-7 7 7" />,
  arrowR: (p: IconProps = {}) => <Icon {...p} d="M4 12h16m-6-6 6 6-6 6" />,
  arrowL: (p: IconProps = {}) => <Icon {...p} d="M20 12H4m6 6-6-6 6-6" />,
  arrowUp: (p: IconProps = {}) => <Icon {...p} d="M12 20V4m-6 6 6-6 6 6" />,
  sun: (p: IconProps = {}) => (
    <Icon {...p}>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M2 12h2M20 12h2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
    </Icon>
  ),
  moon: (p: IconProps = {}) => (
    <Icon {...p} d="M20 14.5A8 8 0 1 1 9.5 4a7 7 0 0 0 10.5 10.5Z" />
  ),
  monitor: (p: IconProps = {}) => (
    <Icon {...p}>
      <rect x="3" y="4" width="18" height="13" rx="2" />
      <path d="M8 21h8M12 17v4" />
    </Icon>
  ),
  trash: (p: IconProps = {}) => (
    <Icon {...p}>
      <path d="M4 7h16M10 7V4h4v3" />
      <path d="M6 7v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V7" />
      <path d="M10 11v6M14 11v6" />
    </Icon>
  ),
  edit: (p: IconProps = {}) => (
    <Icon {...p} d="M4 20h4l11-11-4-4L4 16v4ZM14 5l4 4" />
  ),
  filter: (p: IconProps = {}) => <Icon {...p} d="M3.5 5h17l-6.5 8v6l-4-2v-4L3.5 5Z" />,
  tag: (p: IconProps = {}) => (
    <Icon {...p}>
      <path d="M3 12V4a1 1 0 0 1 1-1h8l9 9-9 9-9-9Z" />
      <circle cx="8" cy="8" r="1.4" fill="currentColor" stroke="none" />
    </Icon>
  ),
  bell: (p: IconProps = {}) => (
    <Icon {...p}>
      <path d="M6 16V11a6 6 0 1 1 12 0v5l1.5 2H4.5L6 16Z" />
      <path d="M10 20a2 2 0 0 0 4 0" />
    </Icon>
  ),
  bolt: (p: IconProps = {}) => (
    <Icon {...p} d="M13.5 2 4 14h6l-1.5 8L20 10h-6l-.5-8Z" />
  ),
  clock: (p: IconProps = {}) => (
    <Icon {...p}>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7.5V12l3 2" />
    </Icon>
  ),
  history: (p: IconProps = {}) => (
    <Icon {...p}>
      <path d="M3.5 12a8.5 8.5 0 1 0 2.5-6L3 9" />
      <path d="M3 4v5h5" />
      <path d="M12 7.5V12l3 2" />
    </Icon>
  ),
  pin: (p: IconProps = {}) => (
    <Icon {...p} d="m12 2 4 4-1.5 1.5 3 3L17 12l-4-4-5 5-1 4 4-1 5-5 4 4 .5-.5-3-3L18 6l-4-4-2 0Z" />
  ),
  question: (p: IconProps = {}) => (
    <Icon {...p}>
      <circle cx="12" cy="12" r="9" />
      <path d="M9.5 9.5a2.5 2.5 0 0 1 5 0c0 1.7-2.5 2-2.5 4" />
      <circle cx="12" cy="17.5" r=".5" fill="currentColor" />
    </Icon>
  ),
  info: (p: IconProps = {}) => (
    <Icon {...p}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v6" />
      <circle cx="12" cy="7.5" r=".6" fill="currentColor" />
    </Icon>
  ),
  warning: (p: IconProps = {}) => (
    <Icon {...p}>
      <path d="M12 3 2 20h20L12 3Z" />
      <path d="M12 10v5" />
      <circle cx="12" cy="18" r=".6" fill="currentColor" />
    </Icon>
  ),
  check2: (p: IconProps = {}) => <Icon {...p} d="m4 12 5 5L20 6" />,
  link: (p: IconProps = {}) => (
    <Icon
      {...p}
      d="M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 1 0-5.7-5.7L11 7M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 1 0 5.7 5.7L13 17"
    />
  ),
  doc: (p: IconProps = {}) => (
    <Icon {...p}>
      <path d="M6 3h8l5 5v12a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Z" />
      <path d="M14 3v5h5" />
    </Icon>
  ),
  bookmark: (p: IconProps = {}) => <Icon {...p} d="M6 3h12v18l-6-4-6 4V3Z" />,
  bookmarkFill: (p: IconProps = {}) => (
    <Icon {...p} fill="currentColor" stroke={0} d="M6 3h12v18l-6-4-6 4V3Z" />
  ),
  more: (p: IconProps = {}) => (
    <Icon {...p}>
      <circle cx="5" cy="12" r="1.4" fill="currentColor" stroke="none" />
      <circle cx="12" cy="12" r="1.4" fill="currentColor" stroke="none" />
      <circle cx="19" cy="12" r="1.4" fill="currentColor" stroke="none" />
    </Icon>
  ),
  command: (p: IconProps = {}) => (
    <Icon {...p} d="M9 5a2 2 0 1 1-2 2v10a2 2 0 1 1 2-2h6a2 2 0 1 1-2 2V7a2 2 0 1 1 2 2H9Z" />
  ),
  logo: (p: IconProps = {}) => {
    const size = p.size || 28;
    return (
      <svg width={size} height={size} viewBox="0 0 32 32" aria-hidden="true">
        <rect width="32" height="32" rx="8" fill="var(--accent)" />
        <circle cx="13" cy="13" r="5" fill="none" stroke="var(--fg-on-accent)" strokeWidth="2.4" />
        <path d="m17 17 5 5" stroke="var(--fg-on-accent)" strokeWidth="2.4" strokeLinecap="round" />
        <circle cx="13" cy="13" r="1.6" fill="var(--fg-on-accent)" />
      </svg>
    );
  },
};

export default I;
